import * as React from "react";
import { useAuth } from "../../../../auth/AuthProvider";
import {
  EnrichmentConfig,
  EnrichmentResponse,
  EnrichmentField,
} from "../../../../api/types";
import { useRole } from "../../RoleContext";
import { getEnrichmentData } from "../../../../api/articleService";
import { readArticleCodes } from "../../../../excel/readArticleCodes";
import { writeEnrichmentData } from "../../../../excel/writeEnrichmentData";
import { insertThumbnails } from "../../../../excel/insertThumbnails";
import EnrichmentForm from "./EnrichmentForm";
import { EnrichmentStep } from "./ProgressIndicator";

const ArticleEnrichmentPage: React.FC = () => {
  const { getAccessToken } = useAuth();
  const { activeRole } = useRole();

  const handleEnrich = React.useCallback(
    async (
      config: EnrichmentConfig,
      signal: AbortSignal,
      reportStep: (step: EnrichmentStep) => void
    ): Promise<EnrichmentResponse> => {
      // Step 1: Read article codes
      reportStep("reading");
      const articleEntries = await readArticleCodes(
        config.startRow,
        config.endRow,
        config.articleCodeColumn
      );

      if (articleEntries.length === 0) {
        return { articles: [], notFound: [], errors: [] };
      }

      // Build row map: articleCode → row number
      const articleRowMap = new Map<string, number>();
      const articleCodes: string[] = [];
      for (const entry of articleEntries) {
        articleRowMap.set(entry.code, entry.row);
        articleCodes.push(entry.code);
      }

      // Step 2: Fetch enrichment data
      reportStep("fetching");
      const accessToken = await getAccessToken();
      const response = await getEnrichmentData(
        {
          codes: articleCodes,
          fields: config.selectedFields,
          role: activeRole!,
          lookupType: config.lookupType,
        },
        accessToken,
        signal
      );

      // Step 3: Write scalar values
      reportStep("writing");
      const scalarFields = config.selectedFields.filter((f) => f !== EnrichmentField.ThumbnailBinaryAsBase64);
      if (scalarFields.length > 0 && response.articles.length > 0) {
        const scalarMapping: Record<string, string> = {};
        for (const f of scalarFields) {
          scalarMapping[f] = config.fieldColumnMapping[f];
        }

        await writeEnrichmentData(
          response.articles,
          scalarMapping,
          articleRowMap,
          config.noChangeIfNoResults,
          config.lookupType
        );
      }

      // Step 4: Insert thumbnails
      if (config.selectedFields.includes(EnrichmentField.ThumbnailBinaryAsBase64)) {
        reportStep("thumbnails");
        const thumbnailColumn = config.fieldColumnMapping[EnrichmentField.ThumbnailBinaryAsBase64];
        await insertThumbnails(
          response.articles,
          articleRowMap,
          thumbnailColumn,
          config.thumbnailSize,
          config.fitToCell,
          config.lookupType
        );
      }

      reportStep("done");
      return response;
    },
    [activeRole, getAccessToken]
  );

  return <EnrichmentForm role={activeRole} onEnrich={handleEnrich} />;
};

export default ArticleEnrichmentPage;
